import { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { X, ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";

export const NewsDetailDialog = ({ item, open, onClose }) => {
  const [imgIdx, setImgIdx] = useState(0);
  if (!item) return null;

  const gallery = item.gallery && item.gallery.length > 0 ? item.gallery : (item.image_url ? [item.image_url] : []);
  const current = gallery[imgIdx] || item.image_url;

  const prev = (e) => { e.stopPropagation(); setImgIdx((i) => (i - 1 + gallery.length) % gallery.length); };
  const next = (e) => { e.stopPropagation(); setImgIdx((i) => (i + 1) % gallery.length); };

  const handleClose = () => { setImgIdx(0); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        data-testid={`news-detail-${item.id}`}
        className="max-w-5xl max-h-[92vh] overflow-y-auto bg-[#FAF9F6] border border-[#D1CFC7] rounded-none p-0"
      >
        <button
          onClick={handleClose}
          data-testid="close-news-detail"
          className="absolute top-4 right-4 z-30 bg-[#FAF9F6] border border-[#D1CFC7] hover:border-[#2C402E] p-2 transition-colors"
          aria-label="Fermer"
        >
          <X size={18} className="text-[#1A1A1A]" />
        </button>

        {/* Main image with carousel */}
        {current && (
          <div className="relative bg-[#1A1A1A]">
            <img src={current} alt={item.title} className="w-full max-h-[60vh] object-contain" />
            {gallery.length > 1 && (
              <>
                <button onClick={prev} data-testid="news-prev" className="absolute left-4 top-1/2 -translate-y-1/2 bg-[#FAF9F6]/90 hover:bg-[#FAF9F6] p-2 transition-colors">
                  <ChevronLeft size={22} className="text-[#1A1A1A]" />
                </button>
                <button onClick={next} data-testid="news-next" className="absolute right-4 top-1/2 -translate-y-1/2 bg-[#FAF9F6]/90 hover:bg-[#FAF9F6] p-2 transition-colors">
                  <ChevronRight size={22} className="text-[#1A1A1A]" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 bg-[#1A1A1A]/70 px-3 py-1.5">
                  {gallery.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setImgIdx(i); }}
                      data-testid={`news-dot-${i}`}
                      className={`w-2 h-2 transition-colors ${i === imgIdx ? "bg-[#FAF9F6]" : "bg-[#FAF9F6]/40"}`}
                    />
                  ))}
                </div>
                <p className="absolute top-3 left-3 bg-[#9E4735] text-[#FAF9F6] text-[10px] tracking-[0.2em] uppercase font-body px-3 py-1">
                  {imgIdx + 1} / {gallery.length}
                </p>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-8 md:p-12">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E4735] font-body mb-4">
            {item.category} · {new Date(item.date).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] leading-tight font-light mb-6">
            {item.title}
          </h2>
          <p className="text-base font-body text-[#1A1A1A] leading-relaxed whitespace-pre-line">
            {item.content || item.excerpt}
          </p>

          {item.attachments && item.attachments.length > 0 && (
            <div data-testid="news-attachments" className="mt-10 pt-8 border-t border-[#D1CFC7]">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E4735] font-body mb-5">
                Documents à télécharger
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {item.attachments.map((att, i) => (
                  <a
                    key={i}
                    href={att.url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`news-attachment-${i}`}
                    className="group flex items-start gap-3 p-4 border border-[#D1CFC7] hover:border-[#2C402E] bg-[#F4F1EA] hover:bg-[#FAF9F6] transition-all"
                  >
                    <FileText size={22} className="text-[#9E4735] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-[#1A1A1A] leading-snug">{att.label}</p>
                      <p className="text-[10px] tracking-wide uppercase text-[#757575] mt-1">
                        {att.type || "PDF"}
                      </p>
                    </div>
                    <Download size={16} className="text-[#4A4A4A] group-hover:text-[#2C402E] mt-1 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
